import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, CreditCardIcon, QrCodeIcon, SmartphoneIcon, ShoppingBagIcon, UserIcon, PhoneIcon, MailIcon, MapPinIcon, CheckIcon, XIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import StatusBadge from '../../components/ui/StatusBadge';
// Mock merchant data
const merchantsData = [{
  id: 1,
  name: 'Bismillah Traders',
  status: 'active'
}, {
  id: 2,
  name: 'Karachi Super Mart',
  status: 'active'
}, {
  id: 3,
  name: 'Chase Value Center Gulshan e Iqbal',
  status: 'active'
}, {
  id: 4,
  name: 'Orient Online Shop',
  status: 'active'
}, {
  id: 5,
  name: 'Cafe Khaas',
  status: 'active'
}, {
  id: 6,
  name: 'Zeen Textile',
  status: 'active'
}, {
  id: 7,
  name: 'Islamabad Metro',
  status: 'active'
}, {
  id: 8,
  name: 'Ginsoy Lahore',
  status: 'active'
}];
// Mock tills data
const tillsData = [{
  id: 'TILL001',
  merchantId: 1,
  merchantName: 'Bismillah Traders',
  paymentMode: 'POS',
  userName: 'Ahmed Khan',
  userId: 'AK001',
  mobileNumber: '+92 300 1234567',
  email: 'ahmed@bismillahtraders.pk',
  location: 'Shop #5, Main Market, Islamabad',
  status: 'active'
}, {
  id: 'TILL009',
  merchantId: 1,
  merchantName: 'Bismillah Traders',
  paymentMode: 'QRC',
  userName: 'Faisal Malik',
  userId: 'FM009',
  mobileNumber: '+92 300 9876543',
  email: 'faisal@bismillahtraders.pk',
  location: 'Branch #2, Blue Area, Islamabad',
  status: 'active'
}, {
  id: 'TILL002',
  merchantId: 2,
  merchantName: 'Karachi Super Mart',
  paymentMode: 'QRC',
  userName: 'Fatima Ali',
  userId: 'FA002',
  mobileNumber: '+92 321 9876543',
  email: 'fatima@karachisupermart.pk',
  location: 'Branch #2, Clifton, Karachi',
  status: 'active'
}
// More tills data can be added here
];
interface Merchant {
  id: number;
  name: string;
  status: string;
}
interface Till {
  id: string;
  merchantId: number;
  merchantName: string;
  paymentMode: string;
  userName: string;
  userId: string;
  mobileNumber: string;
  email: string;
  location: string;
  status: string;
}
const TillForm: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    tillName: '',
    tillLocation: '',
    merchantId: '',
    paymentMode: '',
    deviceSerialNumber: '',
    status: 'active'
  });
  // State for the form
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [merchantTills, setMerchantTills] = useState<Till[]>([]);
  const [selectedTill, setSelectedTill] = useState<Till | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Load data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const till = tillsData.find(t => t.id === id);
      if (till) {
        const merchant = merchantsData.find(m => m.id === till.merchantId);
        setSelectedMerchant(merchant || null);
        setSelectedTill(till);
        setFormData({
          tillName: till.userName,
          tillLocation: till.location,
          merchantId: till.merchantId,
          paymentMode: till.paymentMode,
          deviceSerialNumber: '',
          status: till.status
        });
        // Load merchant tills
        const tills = tillsData.filter(t => t.merchantId === till.merchantId);
        setMerchantTills(tills);
      }
    }
  }, [isEditMode, id]);
  // Handle merchant selection
  const handleMerchantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const merchantId = parseInt(e.target.value);
    if (merchantId) {
      const merchant = merchantsData.find(m => m.id === merchantId);
      setSelectedMerchant(merchant || null);
      // Load tills for selected merchant
      const tills = tillsData.filter(t => t.merchantId === merchantId);
      setMerchantTills(tills);
      // Reset selected till and form data
      setSelectedTill(null);
      setFormData({
        tillName: '',
        tillLocation: '',
        merchantId: '',
        paymentMode: '',
        deviceSerialNumber: '',
        status: 'active'
      });
    } else {
      setSelectedMerchant(null);
      setMerchantTills([]);
      setSelectedTill(null);
    }
  };
  // Handle till selection
  const handleTillSelect = (till: Till) => {
    setSelectedTill(till);
    setFormData({
      tillName: till.userName,
      tillLocation: till.location,
      merchantId: till.merchantId,
      paymentMode: till.paymentMode,
      deviceSerialNumber: '',
      status: till.status
    });
  };
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.tillName) newErrors.tillName = 'Till name is required';
    if (!formData.tillLocation) newErrors.tillLocation = 'Till location is required';
    if (!formData.merchantId) newErrors.merchantId = 'Merchant is required';
    if (!formData.paymentMode) newErrors.paymentMode = 'Payment mode is required';else if (formData.paymentMode === 'POS' || formData.paymentMode === 'MobilePOS') {
      if (!formData.deviceSerialNumber) newErrors.deviceSerialNumber = 'Device serial number is required';
    }
    if (!formData.status) newErrors.status = 'Status is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMerchant) {
      setErrors({
        merchant: 'Please select a merchant'
      });
      return;
    }
    if (validateForm()) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        // Reset form after success
        setTimeout(() => {
          if (!isEditMode) {
            setFormData({
              tillName: '',
              tillLocation: '',
              merchantId: '',
              paymentMode: '',
              deviceSerialNumber: '',
              status: 'active'
            });
            setSelectedTill(null);
          }
          setSubmitSuccess(false);
        }, 3000);
      }, 1500);
    }
  };
  // Handle cancel
  const handleCancel = () => {
    navigate('/tills');
  };
  // Get payment mode icon
  const getPaymentModeIcon = (mode: string) => {
    switch (mode) {
      case 'POS':
        return <CreditCardIcon className="h-5 w-5 text-blue-500" data-id="element-6663" />;
      case 'QRC':
        return <QrCodeIcon className="h-5 w-5 text-purple-500" data-id="element-6664" />;
      case 'SoftPOS':
        return <SmartphoneIcon className="h-5 w-5 text-green-500" data-id="element-6665" />;
      case 'MobilePOS':
        return <ShoppingBagIcon className="h-5 w-5 text-orange-500" data-id="element-6666" />;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-6667">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-6668">
        <div className="flex items-center" data-id="element-6669">
          <button onClick={() => navigate('/tills')} className="mr-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-6670">
            <ArrowLeftIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-6671" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-6672">
            {isEditMode ? 'Edit Till' : 'Add New Till'}
          </h1>
        </div>
      </div>
      <Card data-id="element-6673">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-6674">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6675">
            {/* Till Name */}
            <div data-id="element-6676">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-6677">
                Till Name <span className="text-red-500" data-id="element-6678">*</span>
              </label>
              <input type="text" name="tillName" value={formData.tillName} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter till name" required data-id="element-6679" />
            </div>
            {/* Till Location */}
            <div data-id="element-6680">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-6681">
                Till Location <span className="text-red-500" data-id="element-6682">*</span>
              </label>
              <input type="text" name="tillLocation" value={formData.tillLocation} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter till location" required data-id="element-6683" />
            </div>
            {/* Merchant */}
            <div data-id="element-6684">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-6685">
                Merchant <span className="text-red-500" data-id="element-6686">*</span>
              </label>
              <select name="merchantId" value={formData.merchantId} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-6687">
                <option value="" data-id="element-6688">Select Merchant</option>
                {merchantsData.map(merchant => <option key={merchant.id} value={merchant.id} data-id="element-6689">
                    {merchant.name} ({merchant.id})
                  </option>)}
              </select>
            </div>
            {/* Payment Mode */}
            <div data-id="element-6690">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-6691">
                Payment Mode <span className="text-red-500" data-id="element-6692">*</span>
              </label>
              <select name="paymentMode" value={formData.paymentMode} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-6693">
                <option value="" data-id="element-6694">Select Payment Mode</option>
                <option value="QR" data-id="element-6695">QR</option>
                <option value="POS" data-id="element-6696">POS</option>
                <option value="MobilePOS" data-id="element-6697">Mobile POS</option>
                <option value="Online" data-id="element-6698">Online</option>
              </select>
            </div>
            {/* Device Serial Number - Show only for POS or MobilePOS */}
            {(formData.paymentMode === 'POS' || formData.paymentMode === 'MobilePOS') && <div className="md:col-span-2" data-id="element-6699">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-6700">
                  Device Serial Number{' '}
                  <span className="text-gray-500 text-xs" data-id="element-6701">(Optional)</span>
                </label>
                <input type="text" name="deviceSerialNumber" value={formData.deviceSerialNumber} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter device serial number" data-id="element-6702" />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-6703">
                  Enter the serial number of the POS device if available
                </p>
              </div>}
            {/* Status */}
            <div data-id="element-6704">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-6705">
                Status <span className="text-red-500" data-id="element-6706">*</span>
              </label>
              <select name="status" value={formData.status} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-6707">
                <option value="active" data-id="element-6708">Active</option>
                <option value="inactive" data-id="element-6709">Inactive</option>
              </select>
            </div>
          </div>

          {/* Existing Tills for Selected Merchant */}
          {selectedMerchant && merchantTills.length > 0 && !isEditMode && <div data-id="element-6710">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-6711">
                Existing Tills for {selectedMerchant.name}
              </h3>
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3" data-id="element-6712">
                <div className="max-h-48 overflow-y-auto" data-id="element-6713">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6714">
                    <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-6715">
                      <tr data-id="element-6716">
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6717">
                          Till ID
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6718">
                          Payment Mode
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6719">
                          User
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6720">
                          Location
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6721">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6722">
                      {merchantTills.map(till => <tr key={till.id} className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedTill?.id === till.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} onClick={() => handleTillSelect(till)} data-id="element-6723">
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6724">
                            {till.id}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6725">
                            <div className="flex items-center" data-id="element-6726">
                              {getPaymentModeIcon(till.paymentMode)}
                              <span className="ml-1" data-id="element-6727">{till.paymentMode}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6728">
                            {till.userName}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6729">
                            {till.location.length > 25 ? `${till.location.substring(0, 25)}...` : till.location}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6730">
                            {till.status === 'active' ? <StatusBadge status="success" text="Active" data-id="element-6731" /> : <StatusBadge status="danger" text="Inactive" data-id="element-6732" />}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                {selectedTill && <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md" data-id="element-6733">
                    <div className="flex items-center" data-id="element-6734">
                      <InfoIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" data-id="element-6735" />
                      <p className="text-sm text-blue-700 dark:text-blue-300" data-id="element-6736">
                        Till{' '}
                        <span className="font-medium" data-id="element-6737">{selectedTill.id}</span>{' '}
                        is selected for editing.
                      </p>
                    </div>
                  </div>}
              </div>
            </div>}

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-6738">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-6739">
              {isEditMode || selectedTill ? 'Edit Till Details' : 'New Till Details'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6740">
              {/* Payment Mode */}
              <div data-id="element-6741">
                <label htmlFor="paymentMode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6742">
                  Payment Mode
                </label>
                <select id="paymentMode" name="paymentMode" className={`w-full rounded-md border ${errors.paymentMode ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} value={formData.paymentMode} onChange={handleInputChange} data-id="element-6743">
                  <option value="" data-id="element-6744">Select payment mode</option>
                  <option value="POS" data-id="element-6745">POS</option>
                  <option value="QRC" data-id="element-6746">QRC</option>
                  <option value="SoftPOS" data-id="element-6747">SoftPOS</option>
                  <option value="MobilePOS" data-id="element-6748">MobilePOS</option>
                </select>
                {errors.paymentMode && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6749">
                    {errors.paymentMode}
                  </p>}
              </div>

              {/* User Name */}
              <div data-id="element-6750">
                <label htmlFor="userName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6751">
                  Till User Name
                </label>
                <div className="relative" data-id="element-6752">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6753">
                    <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-6754" />
                  </div>
                  <input type="text" id="userName" name="userName" className={`w-full pl-10 rounded-md border ${errors.userName ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="Enter till user's name" value={formData.userName} onChange={handleInputChange} data-id="element-6755" />
                </div>
                {errors.userName && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6756">
                    {errors.userName}
                  </p>}
              </div>

              {/* User ID */}
              <div data-id="element-6757">
                <label htmlFor="userId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6758">
                  Till User ID
                </label>
                <input type="text" id="userId" name="userId" className={`w-full rounded-md border ${errors.userId ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="Enter till user's ID" value={formData.userId} onChange={handleInputChange} data-id="element-6759" />
                {errors.userId && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6760">
                    {errors.userId}
                  </p>}
              </div>

              {/* Mobile Number */}
              <div data-id="element-6761">
                <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6762">
                  Mobile Number
                </label>
                <div className="relative" data-id="element-6763">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6764">
                    <PhoneIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-6765" />
                  </div>
                  <input type="text" id="mobileNumber" name="mobileNumber" className={`w-full pl-10 rounded-md border ${errors.mobileNumber ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="+92 300 1234567" value={formData.mobileNumber} onChange={handleInputChange} data-id="element-6766" />
                </div>
                {errors.mobileNumber && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6767">
                    {errors.mobileNumber}
                  </p>}
              </div>

              {/* Email */}
              <div data-id="element-6768">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6769">
                  Email
                </label>
                <div className="relative" data-id="element-6770">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6771">
                    <MailIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-6772" />
                  </div>
                  <input type="email" id="email" name="email" className={`w-full pl-10 rounded-md border ${errors.email ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="user@example.com" value={formData.email} onChange={handleInputChange} data-id="element-6773" />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6774">
                    {errors.email}
                  </p>}
              </div>

              {/* Location */}
              <div className="md:col-span-2" data-id="element-6775">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6776">
                  Till Location (Address)
                </label>
                <div className="relative" data-id="element-6777">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6778">
                    <MapPinIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-6779" />
                  </div>
                  <input type="text" id="location" name="location" className={`w-full pl-10 rounded-md border ${errors.location ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="Enter physical address of the till" value={formData.location} onChange={handleInputChange} data-id="element-6780" />
                </div>
                {errors.location && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6781">
                    {errors.location}
                  </p>}
              </div>
            </div>
          </div>

          {/* Notice */}
          <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-6782">
            <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-start" data-id="element-6783">
              <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-6784" />
              <span data-id="element-6785">
                <strong data-id="element-6786">Note:</strong> This request will be submitted for
                approval by a Checker before it is processed. You will be
                notified once the request is approved or rejected.
              </span>
            </p>
          </div>

          {/* Success Message */}
          {submitSuccess && <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded p-3" data-id="element-6787">
              <p className="text-sm text-green-800 dark:text-green-300 flex items-start" data-id="element-6788">
                <CheckIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-6789" />
                <span data-id="element-6790">
                  <strong data-id="element-6791">Success!</strong> Your request has been submitted for
                  approval.
                </span>
              </p>
            </div>}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-6792">
            <button type="button" onClick={handleCancel} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6793">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting || submitSuccess} className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${isSubmitting || submitSuccess ? 'bg-blue-400 dark:bg-blue-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`} data-id="element-6794">
              {isSubmitting ? 'Submitting...' : isEditMode ? 'Update Till' : 'Add Till'}
            </button>
          </div>
        </form>
      </Card>
    </div>;
};
export default TillForm;